import isUndefined from 'lodash/isUndefined';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import serialize from 'form-serialize';
import {getWindow} from 'ssr-window';
import DOMUtils from '@spothero/utils/dom';
import AuthenticationUtils from 'utils/authentication';
import UserUtils from 'user/user-utils';
import withConfig from 'config/withConfig';

const window = getWindow();

export default EnhancedComponent => {
    class WithUserAuthentication extends Component {
        static propTypes = {
            referralSource: PropTypes.string,
            register: PropTypes.bool,
            focus: PropTypes.bool,
            isProduction: PropTypes.bool,
            onUserChange: PropTypes.func.isRequired,
            initRecaptcha: PropTypes.func.isRequired,
        };
        state = {
            register: this.props.register,
            formLoading: false,
            socialAuthLoading: false,
            formGroupError: null,
            emailError: null,
            socialAuthError: null,
            showFacebookEmailModal: false,
            recaptchaError: false,
        };

        constructor(props) {
            super(props);

            this._isStateful = EnhancedComponent.prototype
                ? !isUndefined(EnhancedComponent.prototype.render)
                : false;
        }

        componentDidMount() {
            const {initRecaptcha} = this.props;
            const {register} = this.state;

            if (register) {
                initRecaptcha();
            }
        }

        componentDidUpdate(prevProps, prevState) {
            const {initRecaptcha} = this.props;
            const {register} = this.state;

            if (register && prevState.register !== register) {
                initRecaptcha();
            }
        }

        _onToggleRegisterClick = () => {
            this.setState((prevState, props) => {
                return {
                    register: !prevState.register,
                };
            });
        };

        _onUserFieldChange = () => {
            this.setState({
                formGroupError: null,
                emailError: null,
            });
        };

        _onFacebookClick = () => {
            this._onSocialClick('facebook');
        };

        _onFacebookEmailModalHidden = () => {
            this.setState({
                showFacebookEmailModal: false,
            });
        };

        _onGoogleClick = () => {
            this._onSocialClick('google');
        };

        _onSocialClick(platform) {
            this.setState({
                socialAuthError: null,
                socialAuthLoading: true,
            });

            if (platform === 'facebook') {
                this._authenticateFacebook();
            } else {
                this._authenticateGoogle();
            }
        }

        _onSubmit = evt => {
            const {isProduction} = this.props;
            const {register} = this.state;
            const passwordData = this._userAuthentication.submitPassword();
            const userAuthenticationData = serialize(evt.target, {hash: true});

            if (userAuthenticationData && passwordData) {
                if (register) {
                    const recaptchaResponse = DOMUtils.el(
                        '#g-recaptcha-response'
                    );

                    if (
                        isProduction &&
                        (!recaptchaResponse || !recaptchaResponse.value)
                    ) {
                        this.setState({
                            recaptchaError: true,
                        });

                        return;
                    }
                }

                this.setState({
                    recaptchaError: false,
                    formLoading: true,
                    formGroupError: null,
                });

                const {email, password, remember} = {
                    ...userAuthenticationData,
                    ...passwordData,
                };

                this._authenticate({
                    email,
                    password,
                    remember,
                    promocode: false,
                });
            }
        };

        _authenticate({email, password, remember, promocode}) {
            const {isProduction, onUserChange} = this.props;

            if (this.state.register) {
                const recaptchaResponse = DOMUtils.el('#g-recaptcha-response');

                AuthenticationUtils.register(
                    email,
                    password,
                    promocode || null,
                    false,
                    false,
                    user => {
                        onUserChange({
                            user: {
                                ...user,
                                status: UserUtils.AUTH_STATE.USER,
                            },
                            userNormalized: false,
                        });
                    },
                    error => {
                        const nextState = {
                            formLoading: false,
                            formGroupError: error[0].messages[0],
                        };

                        if (error[0].code === 'email_taken') {
                            nextState.emailError = true;
                        }

                        if (
                            isProduction &&
                            window.grecaptcha &&
                            DOMUtils.el('.g-recaptcha').hasChildNodes()
                        ) {
                            window.grecaptcha.reset();
                        }

                        this.setState(nextState);
                    },
                    recaptchaResponse ? recaptchaResponse.value : null
                );
            } else {
                AuthenticationUtils.logIn(
                    email,
                    password,
                    remember,
                    user => {
                        onUserChange({
                            user: {
                                ...user,
                                status: UserUtils.AUTH_STATE.USER,
                            },
                            userNormalized: false,
                        });
                    },
                    error => {
                        this.setState({
                            formLoading: false,
                            formGroupError: error[0].messages[0],
                        });
                    }
                );
            }
        }

        _authenticateFacebook = ({email = false, promocode = null} = {}) => {
            AuthenticationUtils.logInFacebook({
                email,
                registration: this.state.register,
                referralToken: promocode,
                referralSource: this.props.referralSource,
                onFailure: this._facebookErrorHandler,
            });
        };

        _authenticateGoogle = ({email = false, promocode = null} = {}) => {
            AuthenticationUtils.logInGoogle({
                email,
                registration: this.state.register,
                referralToken: promocode,
                referralSource: this.props.referralSource,
                onFailure: this._googleErrorHandler,
            });
        };

        _facebookErrorHandler = errors => {
            let errorFlag = false;

            if (errors.length) {
                if (errors[0].code === 'missing_email') {
                    this.setState({
                        showFacebookEmailModal: true,
                    });
                } else {
                    errorFlag = true;
                }
            } else {
                errorFlag = true;
            }

            if (errorFlag) {
                this.setState({
                    socialAuthLoading: false,
                    socialAuthError:
                        'Whoops, something went wrong. Please try again.',
                });
            }
        };

        _googleErrorHandler = errors => {
            this.setState({
                socialAuthLoading: false,
                socialAuthError:
                    'Whoops, something went wrong. Please try again.',
            });
        };

        render() {
            const allProps = {
                ...this.props,
            };
            const {
                formLoading,
                formGroupError,
                socialAuthError,
                emailError,
                showFacebookEmailModal,
                socialAuthLoading,
                register,
                recaptchaError,
            } = this.state;

            if (this._isStateful) {
                allProps.ref = node => {
                    this._wrappedInstance = node;
                };
            }

            return (
                <EnhancedComponent
                    {...allProps}
                    ref={node => {
                        this._userAuthentication = node;
                    }}
                    formLoading={formLoading}
                    formGroupError={formGroupError}
                    socialAuthError={socialAuthError}
                    emailError={emailError}
                    showFacebookEmailModal={showFacebookEmailModal}
                    socialAuthLoading={socialAuthLoading}
                    onToggleRegisterClick={this._onToggleRegisterClick}
                    onFacebookClick={this._onFacebookClick}
                    onGoogleClick={this._onGoogleClick}
                    onSubmit={this._onSubmit}
                    onUserFieldChange={this._onUserFieldChange}
                    onFacebookEmailModalHidden={
                        this._onFacebookEmailModalHidden
                    }
                    register={register}
                    recaptchaError={recaptchaError}
                />
            );
        }
    }

    return withConfig(['isProduction', 'initRecaptcha'])(
        WithUserAuthentication
    );
};
